import React, { useState } from 'react';
import { Play, BarChart2, CheckCircle, Sliders } from 'lucide-react';

export default function StrategyBacktester() {
  const [strategy, setStrategy] = useState('EMA Crossover (9/21)');
  const [timeframe, setTimeframe] = useState('15 Minutes');
  const [capital, setCapital] = useState('500000');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);

  const runBacktestSimulation = (e) => {
    e.preventDefault();
    setIsRunning(true);
    setResults(null);

    setTimeout(() => {
      setIsRunning(false);
      setResults({
        totalTrades: 142,
        winRate: '71.8%',
        profitFactor: '2.45',
        netProfit: '₹ 1,24,500',
        maxDrawdown: '-3.4%',
        sharpeRatio: '1.85'
      });
    }, 1200);
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#0B0F19', color: '#FFF', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontSize: '26px', color: '#D4AF37', margin: '0 0 8px 0' }}>
            Advanced Algorithmic Backtester
          </h2>
          <p style={{ color: '#94A3B8', fontSize: '13px', margin: 0 }}>
            Test your trading models against historical market tick data with zero latency simulation.
          </p>
        </div>

        {/* Configuration Card */}
        <div style={{ backgroundColor: '#0F172A', border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: '16px', padding: '25px', marginBottom: '30px' }}>
          <h3 style={{ color: '#FFF', fontSize: '16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sliders size={18} color="#D4AF37" /> Strategy Parameters
          </h3>

          <form onSubmit={runBacktestSimulation} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '12px', marginBottom: '6px' }}>Select Strategy Model</label>
              <select 
                value={strategy} 
                onChange={(e) => setStrategy(e.target.value)}
                style={{ width: '100%', backgroundColor: '#1E293B', color: '#FFF', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '10px', outline: 'none' }}
              >
                <option value="EMA Crossover (9/21)">EMA Crossover (9/21)</option>
                <option value="RSI Mean Reversion">RSI Mean Reversion (14)</option>
                <option value="Volume Breakout">Institutional Volume Breakout</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '12px', marginBottom: '6px' }}>Execution Timeframe</label>
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                style={{ width: '100%', backgroundColor: '#1E293B', color: '#FFF', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '10px', outline: 'none' }}
              >
                <option value="5 Minutes">5 Minutes</option>
                <option value="15 Minutes">15 Minutes</option>
                <option value="1 Hour">1 Hour Daily</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '12px', marginBottom: '6px' }}>Initial Test Capital (₹)</label>
              <input 
                type="number" 
                value={capital} 
                onChange={(e) => setCapital(e.target.value)}
                style={{ width: '100%', backgroundColor: '#1E293B', color: '#FFF', border: '1px solid rgba(255,255,255,0.1)', padding: '12px', borderRadius: '10px', outline: 'none', boxSizing: 'border-box' }} 
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button 
                type="submit" 
                disabled={isRunning}
                style={{ width: '100%', backgroundColor: '#D4AF37', color: '#000', border: 'none', padding: '12px', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '14px' }}
              >
                <Play size={16} /> {isRunning ? 'Running Simulation...' : 'Run Backtest'}
              </button>
            </div>
          </form>
        </div>

        {/* Results Section */}
        {results && (
          <div style={{ backgroundColor: '#0F172A', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '16px', padding: '25px', animation: 'fadeIn 0.3s ease-out' }}>
            <h3 style={{ color: '#10B981', fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={20} /> Backtest Execution Successful
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Total Trades</span>
                <strong style={{ fontSize: '18px', color: '#FFF' }}>{results.totalTrades}</strong>
              </div>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Win Rate</span>
                <strong style={{ fontSize: '18px', color: '#10B981' }}>{results.winRate}</strong>
              </div>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Profit Factor</span>
                <strong style={{ fontSize: '18px', color: '#38BDF8' }}>{results.profitFactor}</strong>
              </div>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Net Profit</span>
                <strong style={{ fontSize: '18px', color: '#10B981' }}>{results.netProfit}</strong>
              </div>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Max Drawdown</span>
                <strong style={{ fontSize: '18px', color: '#EF4444' }}>{results.maxDrawdown}</strong>
              </div>
              <div style={{ backgroundColor: '#1E293B', padding: '16px', borderRadius: '12px' }}>
                <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block' }}>Sharpe Ratio</span>
                <strong style={{ fontSize: '18px', color: '#D4AF37' }}>{results.sharpeRatio}</strong>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}